import authService from '@platform/core/src/services/authService';
import config from '@platform/core/src/config';
import { v4 as uuidv4 } from 'uuid';

const API_BASE_URL = config.API_BASE_URL;
const PLANS_API_URL = `${API_BASE_URL}/api/v1/workout-plans`;

const ensureComponentIds = (planData) => {
  if (!planData) return planData;
  const processedData = JSON.parse(JSON.stringify(planData));
  
  if (processedData.stages && Array.isArray(processedData.stages)) {
    processedData.stages.forEach(stage => {
      if (!stage.component_id) {
        stage.component_id = uuidv4();
      }
      if (stage.activities && Array.isArray(stage.activities)) {
        stage.activities.forEach(activity => {
          if (!activity.component_id) {
            activity.component_id = uuidv4();
          }
          if (activity.actions && Array.isArray(activity.actions)) {
            activity.actions.forEach(action => {
              if (!action.component_id) {
                action.component_id = uuidv4();
              }
            });
          }
        });
      }
    });

    // Ensure stage_order matches stages
    if (!processedData.stage_order) {
      processedData.stage_order = processedData.stages.map(s => s.component_id);
    } else {
      const orderedSet = new Set(processedData.stage_order);
      processedData.stages.forEach(s => {
        if (!orderedSet.has(s.component_id)) {
          processedData.stage_order.push(s.component_id);
        }
      });
    }
  }
  return processedData;
};

/**
 * Fetches all workout plans for the current user.
 * @param {Object} [options={}] - Query options.
 * @param {string} [options.fields] - Comma-separated list of fields to return.
 * @returns {Promise<Array<Object>>} An array of workout plan objects.
 */
export const getWorkoutPlans = async (options = {}) => {
  const url = new URL(PLANS_API_URL);
  if (options.fields) {
    url.searchParams.append('fields', options.fields);
  }

  try {
    const response = await authService.authFetch(url.toString());
    if (!response.ok) throw new Error(`Failed to fetch workout plans: ${response.status}`);
    const data = await response.json();
    
    // MODIFIED: Explicitly tag as partial if fields were requested
    if (options.fields) {
      console.log('[workoutPlanApi] Fetched plans with sparse fields. Tagging as partial.');
      return data.map(plan => ({ ...plan, _isPartial: true }));
    }
    
    return data;
  } catch (error) {
    console.error('[workoutPlanApi] Error fetching plans:', error);
    authService.trackNetworkError('getWorkoutPlans', error);
    throw error;
  }
};

/**
 * Fetches a single workout plan by ID.
 * @param {string} planId - The ID of the workout plan.
 * @returns {Promise<Object>} The workout plan object.
 */
export const getWorkoutPlanById = async (planId) => {
  const url = `${PLANS_API_URL}/${planId}`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) throw new Error(`Failed to fetch plan details: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('[workoutPlanApi] Error fetching single plan:', error);
    authService.trackNetworkError('getWorkoutPlanById', error);
    throw error;
  }
};

export const createWorkoutPlan = async (planData) => {
  const url = `${PLANS_API_URL}`;
  const payload = ensureComponentIds(planData);
  try {
    const response = await authService.authFetch(url, { method: 'POST', body: payload });
    if (!response.ok) throw new Error(`Failed to create workout: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('[workoutPlanApi] Error creating plan:', error);
    authService.trackNetworkError('createWorkoutPlan', error);
    throw error;
  }
};

export const updateWorkoutPlan = async (planId, planData) => {
  const url = `${PLANS_API_URL}/${planId}`;
  const payload = ensureComponentIds(planData);
  try {
    const response = await authService.authFetch(url, { method: 'PUT', body: payload });
    if (!response.ok) throw new Error(`Failed to update workout plan: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('[workoutPlanApi] Error updating plan:', error);
    authService.trackNetworkError('updateWorkoutPlan', error);
    throw error;
  }
};

export const patchWorkoutPlan = async (planId, partialData) => {
  const url = `${PLANS_API_URL}/${planId}`;
  const payload = ensureComponentIds(partialData);
  try {
    const response = await authService.authFetch(url, { method: 'PATCH', body: payload });
    if (!response.ok) throw new Error(`Failed to patch workout plan: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('[workoutPlanApi] Error patching plan:', error);
    authService.trackNetworkError('patchWorkoutPlan', error);
    throw error;
  }
};

export const deleteWorkoutPlan = async (planId) => {
  const url = `${PLANS_API_URL}/${planId}`;
  try {
    const response = await authService.authFetch(url, { method: 'DELETE' });
    if (!response.ok) throw new Error(`Failed to delete workout plan: ${response.status}`);
  } catch (error) {
    console.error('[workoutPlanApi] Error deleting plan:', error);
    authService.trackNetworkError('deleteWorkoutPlan', error);
    throw error;
  }
};

export const completeWorkoutPlan = async (completionData) => {
  const url = `${PLANS_API_URL}/complete`;
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: completionData,
    });
    if (!response.ok) {
      let errorDetail = `Failed to complete workout plan: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutPlanApi] Error completing workout plan:', error);
    authService.trackNetworkError('completeWorkoutPlan', error);
    throw error;
  }
};